import { postFakeLogin } from "helpers/fakebackend_helper";
import { loginError, loginSuccess, logoutSuccess } from "./reducer";
import { ThunkAction } from "redux-thunk";
import { Action, Dispatch } from "redux";
import { RootState } from "../../../index";
import { getFirebaseBackend } from "helpers/firebase_helper";
import { postLogin, postQRLogin } from "helpers/api_method";
import { LogActivity } from "helpers/api_method";

import { toast } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';

interface User {
    email: string;
    password: string;
    location?: {
        ip?: string;
        city?: string;
        region?: string;
        country?: string;
        org?: string;
    };
    deviceInfo?: {
        userAgent?: string;
        device?: string;
        os?: string;
        browser?: string;
        browserVersion?: string;
    };
    systemType?: string;
}

export const loginUser = (
    user: User,
    history: any
): ThunkAction<void, RootState, unknown, Action<string>> => async (dispatch: Dispatch) => {
    try {
        let response: any;
        response = await postLogin({
            identifier: user.email,
            password: user.password,
            location: user.location,
            deviceInfo: user.deviceInfo,
            systemType: user.systemType || "HRMS Web",
        });
        if (response.message === "Email and password are required") {
            dispatch(loginError(response.message));
        } else if (response.message === "Login successful") {
            localStorage.setItem("authUser", JSON.stringify(response.data.access_token));
            localStorage.setItem("authUserRefreshToken", JSON.stringify(response.data.refresh_token));
            localStorage.setItem("userDetails", JSON.stringify(response.data.user));
            localStorage.setItem("loginTimestamp", Date.now().toString());
            if (!localStorage.getItem("passwordChangedAt")) {
                localStorage.setItem("passwordChangedAt", Date.now().toString());
            }
            // Force showing the post-login loader (even if boot is fast)
            sessionStorage.setItem("postLoginLoader", "1");
            
            dispatch(loginSuccess(response));

            // Log login activity (includes URL/context for anomaly tracking)
            try {
                const payload = {
                    activity_type: "LOGIN",
                    activity_description: JSON.stringify({
                        event: "login",
                        url: window.location.href,
                        path: window.location.pathname,
                        referrer: document.referrer || null,
                        timestamp: new Date().toISOString(),
                    }),
                };
                await LogActivity(payload);
            } catch (e) {
                // Non-blocking
            }
            
            // Initialize crashlytics after successful login
            try {
                const { default: CrashlyticsService } = await import("../../../services/crashlyticsService");
                CrashlyticsService.getInstance();
            } catch (error) {
                console.warn('Failed to initialize crashlytics:', error);
            }
            
            // Trigger immediate notification refresh after login
            try {
                const { default: NotificationPollingService } = await import("../../../services/notificationPollingService");
                const notificationService = NotificationPollingService.getInstance();
                
                // Start notification polling and trigger immediate fetch
                await notificationService.startPolling();
                
                // Trigger an immediate refresh to get the login notification
                setTimeout(() => {
                    notificationService.refreshNotifications();
                }, 1000); // Small delay to ensure backend has processed the notification
                
            } catch (error) {
                console.warn('Failed to initialize notification service:', error);
            }
            
            // Redirect all users to dashboard since roles are handled dynamically
            history("/dashboard");
        } else {
            dispatch(loginError(response.message));
        }
    } catch (error: any) {
        // Account lockout (423): pass object for lockout UI and countdown
        if (error && typeof error === "object" && error.isLockout === true) {
            dispatch(loginError({
                isLockout: true,
                message: error.message || "Account temporarily locked. Please try again later.",
                lockoutSecondsRemaining: error.lockoutSecondsRemaining,
            }));
            return;
        }
        // IP block / breach (429): too many failed logins from same network
        if (error && typeof error === "object" && error.isIpBlocked === true) {
            dispatch(loginError({
                isIpBlocked: true,
                message: error.message || "Too many failed login attempts from your network. Access temporarily blocked. Please try again later.",
                retryAfterSeconds: error.retryAfterSeconds,
            }));
            return;
        }
        const message = typeof error === "string" ? error : (error?.message || "Invalid credentials");
        dispatch(loginError(message));
    }
};

export const logoutUser = () => async (dispatch: Dispatch) => {
    try {
        // Log logout activity BEFORE clearing auth (non-blocking)
        try {
            const payload = {
                activity_type: "LOGOUT",
                activity_description: JSON.stringify({
                    event: "logout",
                    url: window.location.href,
                    path: window.location.pathname,
                    referrer: document.referrer || null,
                    timestamp: new Date().toISOString(),
                }),
            };
            await LogActivity(payload);
        } catch (e) {
            // Non-blocking
        }

        // Clear all authentication data
        localStorage.removeItem("authUser");
        localStorage.removeItem("authUserRefreshToken");
        localStorage.removeItem("userDetails");
        localStorage.removeItem("loginTimestamp");
        localStorage.removeItem("passwordChangedAt");

        let fireBaseBackend = await getFirebaseBackend();

        if (process.env.REACT_APP_DEFAULTAUTH === "firebase") {
            const response = fireBaseBackend.logout;
            dispatch(logoutSuccess(response));
        } else {
            dispatch(logoutSuccess(true));
        }
    } catch (error) {
        dispatch(loginError(error));
    }
}


export const socialLogin = (type: any, history: any) => async (dispatch: any) => {
    try {
        let response: any;

        if (process.env.REACT_APP_DEFAULTAUTH === "firebase") {
            const fireBaseBackend = getFirebaseBackend();
            response = fireBaseBackend.socialLoginUser(type);
        }

        const socialData = await response;

        if (socialData) {
            sessionStorage.setItem("authUser", JSON.stringify(socialData));
            dispatch(loginSuccess(socialData));
            history('/dashboard');
        }

    } catch (error) {
        dispatch(loginError(error));
    }
}

export const qrLogin = (
    token: string,
    history: any,
    deviceInfo?: any,
    location?: any
): ThunkAction<void, RootState, unknown, Action<string>> => async (dispatch: Dispatch) => {
    try {
        const response: any = await postQRLogin({
            token,
            deviceInfo,
            location,
            systemType: "HRMS Web"
        });
        
        if (response.message === "Login successful") {
            localStorage.setItem("authUser", JSON.stringify(response.data.access_token));
            localStorage.setItem("authUserRefreshToken", JSON.stringify(response.data.refresh_token));
            localStorage.setItem("userDetails", JSON.stringify(response.data.user));
            localStorage.setItem("loginTimestamp", Date.now().toString());
            if (!localStorage.getItem("passwordChangedAt")) {
                localStorage.setItem("passwordChangedAt", Date.now().toString());
            }
            // Force showing the post-login loader (even if boot is fast)
            sessionStorage.setItem("postLoginLoader", "1");
            
            dispatch(loginSuccess(response));
            
            // Initialize crashlytics after successful login
            try {
                const { default: CrashlyticsService } = await import("../../../services/crashlyticsService");
                CrashlyticsService.getInstance();
            } catch (error) {
                console.warn('Failed to initialize crashlytics:', error);
            }
            
            // Trigger immediate notification refresh after login
            try {
                const { default: NotificationPollingService } = await import("../../../services/notificationPollingService");
                const notificationService = NotificationPollingService.getInstance();
                await notificationService.startPolling();
                setTimeout(() => {
                    notificationService.refreshNotifications();
                }, 1000);
            } catch (error) {
                console.warn('Failed to initialize notification service:', error);
            }
            
            history("/dashboard");
        } else {
            dispatch(loginError(response.message));
        }
    } catch (error) {
        dispatch(loginError(error));
    }
};